import _ from 'lodash';
import React from 'react';

import expansionOpts from 'shared/expansionOptions';

export const simcVersions = [{
  value: 'release',
  name: 'Release',
  description: 'Latest official release',
  public: false,
}, {
  value: 'weekly',
  name: 'Weekly',
  description: 'Built from source every Monday',
  default: true,
  public: true,
}, {
  value: 'nightly',
  name: 'Nightly',
  description: 'Nightly build from source, most up-to-date but low chance of bugs',
  public: true,
}, {
  value: 'beta',
  name: 'Beta',
  description: 'Beta build',
  public: false,
}, {
  value: 'latest',
  name: 'Latest',
  description: (
    'The latest build from SimC. Can change rapidly. Higher chance of bugs.' +
    'Keep an eye on the git hash to avoid confusion.'
  ),
  public: true,
}];

export const SMART_SIM_VALUE = 'smart';
export const SMART_SIM_ITERATIONS = 5000;

export const isSmartIterations = iterations => iterations === SMART_SIM_VALUE;

export const getIterations = (iterations) => {
  return isSmartIterations(iterations) ? SMART_SIM_ITERATIONS : iterations;
};

// This depends on each item being sequentially ordered by ID
const expandConusmable = ({ value, name, shortName, itemId, icon }) => _.map(
  _.reverse(_.range(3)), // 0, 1, 2
  i => ({
    value: `${value}_${i + 1}`,
    name: `${name} (Quality ${i + 1})`,
    shortName: `${shortName} ${i + 1}`,
    itemId: itemId + i,
    icon,
    craftingQuality: i + 1,
  }),
);

const iterationOptionsInternal = [{
  value: 'smart',
  name: 'Smart Sim',
  description: (
    <span>
      Recommended - Raidbots chooses iterations and target error for you. Generally should not be changed.
      {' '}
      <a
        href="https://support.raidbots.com/article/55-smart-sim"
        target="_blank"
        rel="noopener noreferrer"
      >
        More info
      </a>
    </span>
  ),
  public: true,
  default: true,
}, {
  value: 5000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 10000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 15000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 20000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 25000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 30000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 40000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 50000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 60000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 70000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 80000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 90000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 100000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}];

if (process.env.NODE_ENV === 'development') {
  iterationOptionsInternal.unshift({
    value: 2499,
    public: true,
    description: 'DEV ONLY',
  });
  iterationOptionsInternal.unshift({
    value: 999,
    public: true,
    description: 'DEV ONLY',
  });
  iterationOptionsInternal.unshift({
    value: 99,
    public: true,
    description: 'DEV ONLY',
  });
}

export const iterationOptions = iterationOptionsInternal;

export const fightStyles = [{
  value: 'Patchwerk',
  name: 'Patchwerk',
  description: 'No movement, single target',
  public: true,
  default: true,
}, {
  value: 'DungeonSlice',
  name: 'Dungeon Slice',
  description: (
    <span>
      6 minute slice of an M+ dungeon.  Overrides Fight Length.
      Check with your class Discord to see if appropriate for your spec.
    </span>
  ),
  public: true,
  overrides: {
    enemyCount: 1,
    fightLength: 360,
  },
}, {
  value: 'TargetDummy',
  name: 'Target Dummy',
  description: 'Always 100% health, raid buffs/debuffs disabled, no consumables',
  public: true,
  overrides: {
    fightStyle: null,
    bloodlust: 0,
    arcaneIntellect: 0,
    fortitude: 0,
    battleShout: 0,
    mysticTouch: 0,
    chaosBrand: 0,
    windfury: 0,
    markOfTheWild: 0,
    powerInfusion: 0,
    bleeding: 0,
    potion: 'disabled',
    food: 'disabled',
    flask: 'disabled',
    augmentation: 'disabled',
    temporaryEnchant: 'disabled',
    enemies: [{
      name: 'Target Dummy',
      properties: {
        enemy_fixed_health_percentage: 100,
      },
    }],
    customInput: [
      ['dragonflight.voice_of_the_silent_star_enable', 0],
    ],
  },
  disableAdvanced: true,
}, {
  value: 'ExecutePatchwerk',
  name: 'Execute Patchwerk',
  description: 'Target starts at 20% HP',
  public: true,
  overrides: {
    fightStyle: null,
    enemies: [{
      name: 'Execute Range',
      properties: {
        enemy_initial_health_percentage: 20,
      },
    }],
  },
  disableAdvanced: true,
}, {
  value: 'HecticAddCleave',
  name: 'Hectic Add Cleave',
  description: 'Regular add spawns (5 adds up about 40% of the fight), frequent movement. Similar to T15 Horridon',
  public: true,
}, {
  value: 'LightMovement',
  name: 'Light Movement',
  description: 'Infrequent movement - Move 50 yards every 85 seconds',
  public: true,
}, {
  value: 'HeavyMovement',
  name: 'Heavy Movement',
  description: 'Frequent movement - Move 25 yards every 10 seconds',
  public: true,
}, {
  value: 'CastingPatchwerk',
  name: 'Casting Patchwerk',
  description: 'Patchwerk that is always casting, can be useful for simming interrupts',
  public: true,
}, {
  value: 'CleaveAdd',
  name: 'Cleave Add',
  description: 'Single add is up about 60% of the fight',
  public: true,
}, {
  value: 'HelterSkelter',
  name: 'Helter Skelter',
  description: 'Movement every 30 seconds, interrupts every 30 seconds, boss invulnerable every 120 seconds',
  public: false,
}, {
  value: 'Beastlord',
  name: 'Beastlord',
  description: 'Fight with a lot of various add packs up most of the fight. Similar to T17 Beastlord Darmac',
  public: false,
}, {
  value: 'Ultraxion',
  name: 'Ultraxion',
  description: 'Single target fight with player stuns and damage to the raid',
  public: false,
}];
export const fightStylesLower = _.mapValues(fightStyles, style => ({
  ...style,
  value: _.toLower(style.value),
}));


export const fightLengths = [{
  value: 20,
  name: '20 seconds',
  public: true,
}, {
  value: 40,
  name: '40 seconds',
  public: true,
}, {
  value: 60,
  name: '1 minute',
  public: true,
}, {
  value: 90,
  name: '1 minute, 30 seconds',
  public: true,
}, {
  value: 120,
  name: '2 minutes',
  public: true,
}, {
  value: 180,
  name: '3 minutes',
  public: true,
}, {
  value: 240,
  name: '4 minutes',
  public: true,
}, {
  value: 300,
  name: '5 minutes',
  public: true,
  default: true,
}, {
  value: 360,
  name: '6 minutes',
  public: true,
}, {
  value: 420,
  name: '7 minutes',
  public: true,
}, {
  value: 480,
  name: '8 minutes',
  public: true,
}, {
  value: 540,
  name: '9 minutes',
  public: true,
}, {
  value: 600,
  name: '10 minutes',
  public: true,
}];

export const enemyCounts = [{
  value: 1,
  name: '1 Boss',
  description: 'Default and best for single target sims',
  default: true,
}, {
  value: 2,
  name: '2 Bosses',
  description: 'Two stacked bosses up at all times, good for multitarget sims',
}, {
  value: 3,
  name: '3 Bosses',
  description: 'Three stacked bosses up at all times, good for multitarget sims',
}, {
  value: 4,
  name: '4 Bosses',
  description: '',
}, {
  value: 5,
  name: '5 Bosses',
  description: '',
}, {
  value: 6,
  name: '6 Bosses',
  description: '',
}, {
  value: 7,
  name: '7 Bosses',
  description: '',
}, {
  value: 8,
  name: '8 Bosses',
  description: '',
}, {
  value: 9,
  name: '9 Bosses',
  description: '',
}, {
  value: 10,
  name: '10 Bosses',
  description: '',
}];

export const enemyTypes = [{
  value: 'FluffyPillow',
  name: 'Fluffy Pillow',
  description: 'Default boss type, best for most DPS simulations',
  default: true,
  public: true,
}];

export const foods = [{
  value: '',
  name: 'SimC Default',
  default: true,
}, {

  // Dragonflight food

  // feasts

  value: 'hoard_of_draconic_delicacies',
  shortName: 'Hoard of Draconic Delicacies',
  name: 'Hoard of Draconic Delicacies (75 Main Stat)',
  itemId: 197795,
  icon: 'inv_cooking_10_draconicdelicacies',
}, {
  value: 'yusas_hearty_stew',
  shortName: 'Yusa\'s Hearty Stew',
  name: '[NYI] Yusa\'s Hearty Stew (105 Lowest Secondary)',
  itemId: 197793,
  icon: 'inv_cooking_10_heartystew',
  disabled: true,
}, {

  // fortune cookie
  value: 'fated_fortune_cookie',
  shortName: 'Fated Fortune Cookie',
  name: 'Fated Fortune Cookie (75 Main Stat)',
  itemId: 197792,
  icon: 'inv_misc_fortunecookie_color03',
}, {

  // double secondary foods

  value: 'aromatic_seafood_platter',
  shortName: 'Aromatic Seafood Platter',
  name: 'Aromatic Seafood Platter (67 Haste/Vers)',
  itemId: 197783,
  icon: 'inv_misc_food_legion_drogbarstylesalmon',
}, {
  value: 'feisty_fish_sticks',
  shortName: 'Feisty Fish Sticks',
  name: 'Feisty Fish Sticks (67 Haste/Crit)',
  itemId: 197782,
  icon: 'inv_misc_food_164_fish_seadog',
}, {
  value: 'great_cerulean_sea',
  shortName: 'Great Cerulean Sea',
  name: 'Great Cerulean Sea (67 Vers/Mastery)',
  itemId: 197787,
  icon: 'inv_misc_food_159_fish_white',
}, {
  value: 'revenge_served_cold',
  shortName: 'Revenge, Served Cold',
  name: 'Revenge, Served Cold (67 Crit/Vers)',
  itemId: 197785,
  icon: 'inv_cooking_100_revengeservedcold_color02',
}, {
  value: 'sizzling_seafood_medley',
  shortName: 'Sizzling Seafood Medley',
  name: 'Sizzling Seafood Medley (67 Haste/Mastery)',
  itemId: 197784,
  icon: 'inv_misc_food_draenor_sturgeonstew',
}, {
  value: 'thousandbone_tongueslicer',
  shortName: 'Thousandbone Tongueslicer',
  name: 'Thousandbone Tongueslicer (67 Crit/Mastery)',
  itemId: 197786,
  icon: 'inv_misc_food_154_fish_77',
}, {

  // single secondary foods

  value: 'filet_of_fangs',
  shortName: 'Filet of Fangs',
  name: 'Filet of Fangs (105 Crit)',
  itemId: 197779,
  icon: 'inv_misc_food_cooked_eternalblossomfish',
}, {
  value: 'timely_demise',
  shortName: 'Timely Demise',
  name: 'Timely Demise (105 Haste)',
  itemId: 197778,
  icon: 'inv_misc_food_legion_seedbatteredfishplate',
}, {
  value: 'saltbaked_fishcake',
  shortName: 'Salt-Baked Fishcake',
  name: 'Salt-Baked Fishcake (105 Mastery)',
  itemId: 197781,
  icon: 'inv_misc_food_legion_deepfriedmossgill',
}, {
  value: 'seamoth_surprise',
  shortName: 'Seamoth Surprise',
  name: 'Seamoth Surprise (105 Vers)',
  itemId: 197780,
  icon: 'inv_misc_food_159_fish_82',
}, {

  // Shadowlands Food

  value: 'feast_of_gluttonous_hedonism',
  shortName: 'Feast of Gluttonous Hedonism',
  name: 'Shadowlands - Feast of Gluttonous Hedonism (20 Main Stat)',
  itemId: 172043,
  icon: 'inv_tradeskill_cooking_shadowlandsfeast_large01',
}, {
  value: 'surprisingly_palatable_feast',
  shortName: 'Surprisingly Palatable Feast',
  name: 'Shadowlands - Surprisingly Palatable Feast (18 Main Stat)',
  itemId: 172042,
  icon: 'inv_tradeskill_cooking_shadowlandsfeast_small01',
}, {

  value: 'smothered_shank',
  shortName: 'Smothered Shank',
  name: 'Shadowlands - Smothered Shank (Fire Breath)',
  itemId: 172062,
  icon: 'inv_cooking_90_smotheredshank',
}, {

  value: 'spinefin_souffle_and_fries',
  shortName: 'Spinefin Souffle and Fries',
  name: 'Shadowlands - Spinefin Souffle and Fries (30 Crit)',
  itemId: 172041,
  icon: 'inv_cooking_90_phantasmalsoufflefries',
}, {
  value: 'tenebrous_crown_roast_aspic',
  shortName: 'Tenebrous Crown Roast Aspic',
  name: 'Shadowlands - Tenebrous Crown Roast Aspic (30 Haste)',
  itemId: 172045,
  icon: 'inv_cooking_90_tenebrouscrownroastaspic',
}, {
  value: 'iridescent_ravioli_with_apple_sauce',
  shortName: 'Iridescent Ravioli with Apple Sauce',
  name: 'Shadowlands - Iridescent Ravioli with Apple Sauce (30 Mastery)',
  itemId: 172049,
  icon: 'inv_cooking_90_crawlerravioliapplesauce',
}, {
  value: 'steak_a_la_mode',
  shortName: 'Steak a la Mode',
  name: 'Shadowlands - Steak a la Mode (30 Vers)',
  itemId: 172051,
  icon: 'inv_cooking_90_steakalamode',
}, {
  value: 'banana_beef_pudding',
  shortName: 'Banana Beef Pudding',
  name: 'Shadowlands - Banana Beef Pudding (22 Stam)',
  itemId: 172069,
  icon: 'inv_cooking_90_bananabeefpudding',
}, {

  value: 'butterscotch_marinated_ribs',
  shortName: 'Butterscotch Marinated Ribs',
  name: 'Shadowlands - Butterscotch Marinated Ribs (18 Crit)',
  itemId: 172040,
  icon: 'inv_cooking_90_butterscotchmarinatedribs',
}, {
  value: 'cinnamon_bonefish_stew',
  shortName: 'Cinnamon Bonefish Stew',
  name: 'Shadowlands - Cinnamon Bonefish Stew (18 Haste)',
  itemId: 172044,
  icon: 'inv_cooking_90_cinnamonbonefishstew',
}, {
  value: 'meaty_apple_dumplings',
  shortName: 'Meaty Apple Dumplings',
  name: 'Shadowlands - Meaty Apple Dumplings (18 Mastery)',
  itemId: 172048,
  icon: 'inv_cooking_90_meatyappledumplings',
}, {
  value: 'sweet_silvergill_sausages',
  shortName: 'Sweet Silvergill Sausages',
  name: 'Shadowlands - Sweet Silvergill Sausages (18 Vers)',
  itemId: 172050,
  icon: 'inv_cooking_90_silvergillsausage',
}, {
  value: 'pickled_meat_smoothie',
  shortName: 'Pickled Meat Smoothie',
  name: 'Shadowlands - Pickled Meat Smoothie (14 Stam)',
  itemId: 172068,
  icon: 'inv_cooking_90_pickledmeatsmoothie',
}, {
  value: 'disabled',
  name: 'No Food',
  shortName: 'No Food',
}];

export const potions = [{
  value: '',
  name: 'SimC Default',
  default: true,
},

// Dragonflight Potions

...expandConusmable({
  value: 'elemental_potion_of_ultimate_power',
  name: 'Elemental Potion of Ultimate Power',
  shortName: 'Elemental Ultimate Power',
  itemId: 191381,
  icon: 'trade_alchemy_dpotion_b20',
}),
...expandConusmable({
  value: 'elemental_potion_of_power',
  name: 'Elemental Potion of Power',
  shortName: 'Elemental Power',
  itemId: 191387,
  icon: 'trade_alchemy_dpotion_b10',
}),
...expandConusmable({
  value: 'bottled_putrescence',
  name: 'Bottled Putrescence',
  shortName: 'Bottled Putrescence',
  itemId: 191360,
  icon: 'inv_10_alchemy_bottle_shape1_black',
}),
...expandConusmable({
  value: 'potion_of_chilled_clarity',
  name: 'Potion of Chilled Clarity',
  shortName: 'Chilled Clarity',
  itemId: 191366,
  icon: 'inv_10_alchemy_bottle_shape4_green',
}),
...expandConusmable({
  value: 'potion_of_shocking_disclosure',
  name: 'Potion of Shocking Disclosure',
  shortName: 'Shocking Disclosure',
  itemId: 191399,
  icon: 'inv_10_alchemy_bottle_shape4_green',
}),

// Shadowlands Potions
{
  value: 'potion_of_deathly_fixation',
  name: 'Shadowlands - Potion of Deathly Fixation',
  shortName: 'Deathly Fixation',
  itemId: 171351,
  icon: 'inv_alchemy_90_combat1_yellow',
}, {
  value: 'potion_of_empowered_exorcisms',
  name: 'Shadowlands - Potion of Empowered Exorcisms',
  shortName: 'Empowered Exorcisms',
  itemId: 171352,
  icon: 'inv_alchemy_90_combat1_pink',
}, {
  value: 'potion_of_hardened_shadows',
  name: 'Shadowlands - Potion of Hardened Shadows',
  shortName: 'Hardened Shadows',
  itemId: 171271,
  icon: 'inv_alchemy_90_combat2_blue',
}, {
  value: 'potion_of_phantom_fire',
  name: 'Shadowlands - Potion of Phantom Fire',
  shortName: 'Phantom Fire',
  itemId: 171349,
  icon: 'inv_alchemy_90_combat1_green',
}, {
  value: 'potion_of_spectral_agility',
  name: 'Shadowlands - Potion of Spectral Agility',
  shortName: 'Spectral Agility',
  itemId: 171270,
  icon: 'inv_alchemy_90_combat2_green',
}, {
  value: 'potion_of_spectral_intellect',
  name: 'Shadowlands - Potion of Spectral Intellect',
  shortName: 'Spectral Intellect',
  itemId: 171273,
  icon: 'inv_alchemy_90_combat2_purple',
}, {
  value: 'potion_of_spectral_strength',
  name: 'Shadowlands - Potion of Spectral Strength',
  shortName: 'Spectral Strength',
  itemId: 171275,
  icon: 'inv_alchemy_90_combat2_yellow',
}, {
  value: 'potion_of_spectral_stamina',
  name: 'Shadowlands - Potion of Spectral Stamina',
  shortName: 'Spectral Stamina',
  itemId: 171274,
  icon: 'inv_alchemy_90_combat2_red',
}, {

  value: 'disabled',
  name: 'No Potion',
  shortName: 'No Potion',
}];

export const flasks = [{
  value: '',
  name: 'SimC Default',
  default: true,

},

// Dragonflight

// non-damage phials
// Phial of the Eye in the Storm
// Phial of Still Air
// Phial of Icy Preservation

...expandConusmable({
  value: 'iced_phial_of_corrupting_rage',
  name: 'Iced Phial of Corrupting Rage',
  shortName: 'Corrupting Rage',
  itemId: 191327,
  icon: 'inv_10_alchemy_bottle_shape2_red',
}),
...expandConusmable({
  value: 'phial_of_charged_isolation',
  name: 'Phial of Charged Isolation',
  shortName: 'Charged Isolation',
  itemId: 191330,
  icon: 'inv_10_alchemy_bottle_shape3_white',
}),
...expandConusmable({
  value: 'phial_of_glacial_fury',
  name: 'Phial of Glacial Fury',
  shortName: 'Glacial Fury',
  itemId: 191333,
  icon: 'inv_10_alchemy_bottle_shape2_white',
}),
...expandConusmable({
  value: 'phial_of_static_empowerment',
  name: 'Phial of Static Empowerment',
  shortName: 'Static Empowerment',
  itemId: 191336,
  icon: 'inv_10_alchemy_bottle_shape2_white',
}),
...expandConusmable({
  value: 'phial_of_elemental_chaos',
  name: 'Phial of Elemental Chaos',
  shortName: 'Elemental Chaos',
  itemId: 191357,
  icon: 'inv_10_alchemy_bottle_shape2_orange',
}),
...expandConusmable({
  value: 'phial_of_tepid_versatility',
  name: 'Phial of Tepid Versatility',
  shortName: 'Tepid Versatility',
  itemId: 191339,
  icon: 'inv_10_alchemy_bottle_shape2_black',
}),

// Shadowlands flasks
{
  value: 'spectral_flask_of_power',
  name: 'Shadowlands - Spectral Flask of Power',
  shortName: 'Spectral Power',
  itemId: 171276,
}, {
  value: 'spectral_flask_of_stamina',
  name: 'Shadowlands - Spectral Flask of Stamina',
  shortName: 'Spectral Stamina',
  itemId: 171278,

}, {
  value: 'disabled',
  name: 'No Flask',
  shortName: 'No Flask',
}];

export const augmentations = [{
  value: '',
  name: 'SimC Default',
  default: true,
}, {
  value: 'draconic_augment_rune',
  name: 'Draconic Augment Rune',
  shortName: 'Draconic',
  itemId: 201325,
}, {
  value: 'veiled_augment_rune',
  name: 'Veiled Augment Rune (Shadowlands)',
  shortName: 'Veiled',
  itemId: 181468,
}, {
  value: 'disabled',
  name: 'Disabled',
  shortName: 'No Rune',
}];

export const augmentationItems = [{
  value: 'draconic_augment_rune',
  name: 'Draconic Augment Rune',
  itemId: 201325,
}, {
  value: 'veiled_augment_rune',
  name: 'Veiled Augment Rune',
  itemId: 181468,
}, {
  value: 'battlescarred_augmentation',
  name: 'Battle-Scarred Augment Rune',
  itemId: 160053,
}];

export const expansionOptions = expansionOpts;
